'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { useState } from 'react';

export interface Agent {
  name: string;
  email: string;
  phone: string;
  image?: string;
  initials?: string;
}

export interface StoneInvestmentPropertyContactCardProps {
  propertyName?: string;
  propertyReference?: string;
  agents?: Agent[];
  onSubmit?: (data: Record<string, string>) => Promise<void>;
  messages?: Record<string, unknown>;
}

/**
 * Stone Investment Property Contact Card
 * 
 * Enquiry form for property pages with:
 * - Agent information display
 * - Elegant form styling
 * - Square corners
 */
export function StoneInvestmentPropertyContactCard({
  propertyName = 'This property',
  propertyReference,
  agents,
  onSubmit,
  messages: messagesRaw,
}: StoneInvestmentPropertyContactCardProps) {
  const stoneMessages = (messagesRaw?.stone || {}) as Record<string, unknown>;
  const formMsg = (stoneMessages.contactForm || {}) as Record<string, string>;
  const contactMsg = ((stoneMessages.contact as Record<string, unknown>)?.form || {}) as Record<string, string>;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: `${formMsg.inquiryAbout?.replace('{{propertyName}}', propertyName) || `I am interested in ${propertyName}`}${propertyReference ? ` (Ref: ${propertyReference})` : ''}.`,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
      }
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Default agents based on Stone Investment experts
  const defaultAgents: Agent[] = [
    {
      name: 'Pierre Monville',
      email: 'p.monville@stoneinvestment.fr',
      phone: '+33 6 16 64 16 04',
      initials: 'PM',
      image: 'https://media.routeroyal.com/7q939t5U8kw4KYfV0-f1Z4Ie3jx_2Z_bxhJmMwd2YrA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZmVmMmU3ZWMzMGI0MjI3ODRjNjE4N2E2MTgzMDkwMjliMzM2YjlhYmY3ODllYWY5OTMyZjAyYzc0OThhYjZmYi5qcGc.jpg',
    },
    {
      name: 'Noël Castinel',
      email: 'n.castinel@stoneinvestment.fr',
      phone: '+33 6 12 56 46 20',
      initials: 'NC',
      image: 'https://media.routeroyal.com/dQlC868U6lS3Bq0IapSA8pI5UMj4Ri92rhUYH8yoHKw/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZTEzMmMzY2EwNDgzZjg4NzY4YjllMTUxNGM0Y2E1NjcyODE4M2M3YzNjMjAyNWRiZjU3ZGQ1MzNmNjdlYzMxOS5qcGc.jpg',
    },
    {
      name: 'Bertrand Meunier',
      email: 'b.meunier@stoneinvestment.fr',
      phone: '+230 5 493 95 90',
      initials: 'BM',
      image: 'https://media.routeroyal.com/t5YRo1_Vuz4-4xonmUalT6kLE6pS3rsYE91pUTc26EQ/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZjZiZGFhNDVkZjY3NGZlMjY5OWRmYWVjNTc5MTJmMzE4MDU2Y2ZlNWFiZmMzYTQ5ZmM1MDg3NmViM2RlMDhhMC5qcGc.jpg',
    },
  ];

  const displayAgents = agents || defaultAgents;

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[var(--color-surface)] border border-[var(--color-border)] p-10 text-center"
        id="contact"
      >
        <div className="w-16 h-16 border border-[var(--color-text)] mx-auto mb-6 flex items-center justify-center">
          <svg className="w-8 h-8 text-[var(--color-text)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 
          className="text-2xl text-[var(--color-text)] mb-4"
          style={{ 
            fontFamily: "'Cormorant Garamond', 'Georgia', serif",
            fontWeight: 400,
          }}
        >
          {formMsg.successTitle || 'Thank you for your enquiry'}
        </h3>
        <p 
          className="text-[var(--color-text-muted)]"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {formMsg.successMessage || 'We will be in touch shortly to discuss this property.'}
        </p>
      </motion.div>
    );
  }

  return (
    <div 
      className="bg-[var(--color-surface)] border border-[var(--color-border)]"
      id="contact"
    >
      {/* Header */}
      <div className="p-8 border-b border-[var(--color-border)]">
        <h3 
          className="text-xl text-[var(--color-text)] mb-2"
          style={{ 
            fontFamily: "'Cormorant Garamond', 'Georgia', serif",
            fontWeight: 400,
          }}
        >
          {contactMsg.title || formMsg.getInTouch || 'Enquire About This Property'}
        </h3>
        <p 
          className="text-sm text-[var(--color-text-muted)]"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {contactMsg.subtitle || 'Contact our team for more information or to arrange a viewing.'}
        </p>
      </div>

      {/* Agents */}
      <div className="p-8 border-b border-[var(--color-border)]">
        <p 
          className="text-xs tracking-[0.2em] uppercase text-[var(--color-text-muted)] mb-6"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {contactMsg.yourContacts || 'Your Contacts'}
        </p>
        <div className="space-y-6">
          {displayAgents.map((agent, index) => (
            <div key={index} className="flex items-start gap-4">
              {agent.image ? (
                <div className="w-12 h-12 relative overflow-hidden flex-shrink-0">
                  <Image
                    src={agent.image}
                    alt={agent.name}
                    fill
                    className="object-cover grayscale"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 border border-[var(--color-border)] flex items-center justify-center flex-shrink-0">
                  <span 
                    className="text-xs text-[var(--color-text-muted)]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {agent.initials || agent.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
              )}
              <div>
                <p 
                  className="text-sm text-[var(--color-text)] font-medium mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {agent.name}
                </p>
                <a 
                  href={`mailto:${agent.email}`}
                  className="block text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors mb-0.5"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {agent.email}
                </a>
                <a 
                  href={`tel:${agent.phone.replace(/\s/g, '')}`}
                  className="block text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {agent.phone}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-8">
        <div className="space-y-6">
          <div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={contactMsg.yourName || formMsg.nameLabel || 'Your Name *'}
              required
              className="w-full bg-transparent border-b border-[var(--color-border)] py-3 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-text)] focus:outline-none transition-colors duration-300 text-sm"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={contactMsg.yourEmail || formMsg.emailLabel || 'Email Address *'}
              required
              className="w-full bg-transparent border-b border-[var(--color-border)] py-3 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-text)] focus:outline-none transition-colors duration-300 text-sm"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
          <div>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={contactMsg.yourPhone || formMsg.phoneLabel || 'Phone Number'}
              className="w-full bg-transparent border-b border-[var(--color-border)] py-3 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-text)] focus:outline-none transition-colors duration-300 text-sm"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
          <div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={contactMsg.yourMessage || formMsg.messageLabel || 'Your Message'}
              rows={3}
              className="w-full bg-transparent border-b border-[var(--color-border)] py-3 text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-text)] focus:outline-none transition-colors duration-300 resize-none text-sm"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full mt-8 flex items-center justify-center gap-3 px-8 py-4 bg-[var(--color-text)] text-[var(--color-background)] text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:bg-[var(--color-text)]/90 disabled:opacity-50"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          <span>{isSubmitting ? (formMsg.sending || 'Sending...') : (contactMsg.sendMessage || formMsg.submit || 'Send Enquiry')}</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </form>
    </div>
  );
}
